import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import apiService from 'services/api'
import { DealSearchResult } from 'models/emailIntegration'

interface DealSearchSubContentProps {
  contactCompanyId: number
  onAssign: (deal: DealSearchResult) => void
}

/**
 * Searchable deal list for embedding inside a DropdownMenuSubContent.
 * Click a deal to immediately assign it — no separate confirm button.
 */
export function DealSearchSubContent({
  contactCompanyId,
  onAssign,
}: DealSearchSubContentProps) {
  const api = apiService()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 300)

  const { data: deals, isLoading } = useQuery({
    queryKey: ['deal-search', contactCompanyId, debouncedSearch],
    queryFn: ({ signal }) =>
      api.emailIntegration.searchDeals(
        {
          contact_company_id: contactCompanyId,
          ...(debouncedSearch ? { q: debouncedSearch } : {}),
        },
        signal
      ),
  })

  const results = deals ?? []

  return (
    <div className="w-64">
      {/* Search input */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200">
        <PiMagnifyingGlass className="size-4 text-neutral-400 flex-shrink-0" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search deals..."
          className="flex-1 text-sm outline-none bg-transparent placeholder:text-neutral-400"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>

      {/* Results */}
      <div className="max-h-48 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="px-3 py-4 text-sm text-neutral-500 text-center">
            Loading...
          </div>
        ) : results.length === 0 ? (
          <div className="px-3 py-4 text-sm text-neutral-500 text-center">
            No deals found
          </div>
        ) : (
          results.map((deal: DealSearchResult) => (
            <button
              key={deal.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAssign(deal)
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg',
                'hover:bg-neutral-100 transition-colors cursor-pointer'
              )}
            >
              <div className="flex-1 min-w-0">
                <span className="block truncate font-medium text-neutral-800">
                  {deal.deal_name}
                </span>
                <span className="block truncate text-xs text-neutral-500">
                  Deal #{deal.identifier}
                  {deal.sales_stage_name && ` · ${deal.sales_stage_name}`}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )
}
